import { format } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Locales } from 'shared/assets/localization';
import { TimeRangeContextMenu } from 'shared/components/ui/time-range-context-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Timeslot } from 'shared/types/timeslot';
import { formatDurationLabel } from 'shared/utils/format-duration-label';

import { useData } from '../../use-data';
import * as Styled from './upcoming-week.style';

export type TimeButtonProps = {
  value?: Timeslot | null;
  onChange: (value: Timeslot | null) => void;
};

export const TimeButton: React.FC<TimeButtonProps> = ({ value, onChange }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const { timeFormat } = useData();
  const { openMenu, menuOpen, closeMenu } = useOpenMenu();

  const label = useMemo(() => {
    if (!value) {
      return t('general.labels.all-day');
    }

    const startDate = new Date();
    startDate.setHours(0, 0, 0, value?.startTime ?? 0);
    const endDate = new Date();
    endDate.setHours(0, 0, 0, (value?.startTime ?? 0) + (value?.duration ?? 0));

    return [
      value?.startTime ? format(startDate, timeFormat) : undefined,
      value?.duration
        ? value.startTime
          ? format(endDate, timeFormat)
          : formatDurationLabel(value.duration, locale === Locales.NlNL)
        : undefined,
    ]
      .filter(Boolean)
      .join('-');
  }, [locale, t, timeFormat, value]);

  return (
    <>
      <Styled.TimeButton ref={buttonRef} type="button" onClick={openMenu}>
        {label}
      </Styled.TimeButton>
      {menuOpen && (
        <TimeRangeContextMenu
          referenceElement={buttonRef}
          onChange={onChange}
          onClose={closeMenu}
          timeFormat={timeFormat}
          value={value}
        />
      )}
    </>
  );
};
